import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, catchError, map, mergeMap, Observable, of, shareReplay, Subscription, take, switchMap, tap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { AIApi } from '@api/ai/AI-api';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { MentionSearchService } from '@Monitor/Core/services/mention-search.service';
import { CompletionLengthEnum } from '@api/ai/enums/completion-length.enum';
import { AiAnalysisTypes } from '@Common/Mentions/enums/ai-analysis-types.enum';
import { CustomInsights } from '../models/custom-insights.model';
import { CoverageInsights } from '../models/coverage-insights.model';
import { KeyMessageGroupApi } from '../../../api/ai/model/key-message-group-api.';
import { MentionSearchCriteriaModel } from '../models/mention-search-criteria.model';
import { KeyMessageApi } from '../../../api/ai/model/key-message-api.';
import { sentimentOpinionLengthOptions } from '../../../Common/Forms/data/prompt-length-options';

export const pickRandomItems = <T,> (arr: Iterable<T>, n: number): T[] => {
    const shuffled = Array.from(arr).sort(() => 0.5 - Math.random());
    return shuffled.slice(0, n);
};

export enum InsightContext {
    CustomPromptDialog = 'CustomPromptDialog',
    AiInsight  = 'AiInsight'
}

@Injectable()
export class CoverageAnalysisService implements OnDestroy {

    protected generatedContentSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public generatedContent$ = this.generatedContentSubject.asObservable();

    protected refreshCustomInsightsSubject: BehaviorSubject<void> = new BehaviorSubject<void>(null);
    public customInsights$: Observable<CustomInsights[]> = this.refreshCustomInsightsSubject.asObservable()
        .pipe(
            mergeMap(() => this.aiApi.getCustomAnalysis()),
            map(insights => insights.map(insight => new CustomInsights(insight))),
            shareReplay(1)
        );

    protected subscriptions: Subscription[] = [];

    readonly messageTemplates: Record<InsightContext, any> = {
        [InsightContext.AiInsight]: {
            create: {
                summary: {
                    success: 'Ai.analysis.customInsight.growl.header',
                    fail: 'Ai.analysis.customInsight.error.header'
                },
                detail: {
                    success: 'Ai.analysis.customInsight.message',
                    fail: 'Ai.analysis.customInsight.error.message'
                }
            },
            update: {
                summary: {
                    success: 'Ai.analysis.customInsight.growl.header',
                    fail: 'Ai.analysis.customInsight.error.header'
                },
                detail: {
                    success: 'Ai.analysis.customInsight.message',
                    fail: 'Ai.analysis.customInsight.error.message'
                }
            },
            delete: {
                summary: {
                    success: 'Ai.analysis.customInsight.delete.header',
                    fail: 'Ai.analysis.customInsight.delete.header'
                },
                detail: {
                    success: 'Ai.analysis.customInsight.delete.success.message',
                    fail: 'Ai.analysis.customInsight.delete.error.message'
                }
            },
            duplicate: {
                summary: {
                    create: 'Ai.analysis.customInsight.growl.duplicateName.create',
                    update: 'Ai.analysis.customInsight.growl.duplicateName.update'
                },
                detail: 'Ai.analysis.customInsight.growl.duplicateName.message'
            }
        },
        [InsightContext.CustomPromptDialog]: {
            create: {
                summary: {
                    success: 'CustomPrompt.prompt.growl.create.success.header',
                    fail: 'CustomPrompt.prompt.growl.create.error.header'
                },
                detail: {
                    success: 'CustomPrompt.prompt.growl.create.success.message',
                    fail: 'CustomPrompt.prompt.growl.create.error.message'
                }
            },
            update: {
                summary: {
                    success: 'CustomPrompt.prompt.growl.update.success.header',
                    fail: 'CustomPrompt.prompt.growl.update.error.header'
                },
                detail: {
                    success: 'CustomPrompt.prompt.growl.update.success.message',
                    fail: 'CustomPrompt.prompt.growl.update.error.message'
                }
            },
            delete: {
                summary: {
                    success: 'CustomPrompt.prompt.growl.delete.success.header',
                    fail: 'CustomPrompt.prompt.growl.delete.error.header'
                },
                detail: {
                    success: 'CustomPrompt.prompt.growl.delete.success.message',
                    fail: 'CustomPrompt.prompt.growl.delete.error.message'
                }
            },
            duplicate: {
                summary: {
                    create: 'CustomPrompt.prompt.growl.create.error.header',
                    update: 'CustomPrompt.prompt.growl.update.error.header'
                },
                detail: 'CustomPrompt.prompt.growl.duplicateName.message'
            }
        },
    };

    constructor(
        public aiApi: AIApi,
        public mentionSearchService: MentionSearchService,
        public coreDialogService: CoreDialogService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
    ) {
    }

    getGeneratedContent(): Observable<string> {
        return this.generatedContent$;
    }

    clearGeneratedContent() {
        this.generatedContentSubject.next('');
    }

    setDefaults(coverageInsight: CoverageInsights): CoverageInsights {
        if (coverageInsight.aiAnalysisType === AiAnalysisTypes.EXECUTIVE_SUMMARY) {
            if (!coverageInsight.selectedLengthOption) {
                coverageInsight.selectedLengthOption = coverageInsight.lengthOptions.find(option => option.value === CompletionLengthEnum.MEDIUM);
            }
            coverageInsight = coverageInsight.clone();
        } else if (coverageInsight.aiAnalysisType === AiAnalysisTypes.KEY_TAKEAWAYS || coverageInsight.aiAnalysisType === AiAnalysisTypes.TOP_NARRATIVES) {
            if (!coverageInsight.numOfTakeaways) {
                coverageInsight.numOfTakeaways = 4;
            }
            coverageInsight = coverageInsight.clone();
        } else if (coverageInsight.aiAnalysisType === AiAnalysisTypes.SENTIMENT_OPINION) {
            coverageInsight = coverageInsight.clone();

            coverageInsight.lengthOptions = sentimentOpinionLengthOptions;

            const currentValue = coverageInsight.selectedLengthOption?.value ?? CompletionLengthEnum.MEDIUM;

            coverageInsight.selectedLengthOption = coverageInsight.lengthOptions.find(
                option => option.value === currentValue
            );
        }
        if (!coverageInsight.selectedLanguageOption) {
            coverageInsight.selectedLanguageOption = coverageInsight.languageOptions.find(option => option.value === 'en-US');
        }
        return coverageInsight;
    }

    createCustomInsight(customInsight: CustomInsights, messageContext = InsightContext.AiInsight, emitSuccessMsg: boolean = true): Observable<number> {
        return this.hasDuplicateCustomInsightName(customInsight).pipe(
            switchMap(isDuplicate => {
                if (isDuplicate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform(this.messageTemplates[messageContext].duplicate.summary.create, {
                            name: customInsight.name
                        }),
                        detail: this.localizationService.transform(this.messageTemplates[messageContext].duplicate.detail, {
                            name: customInsight.name
                        }),
                    });

                    return of(null);
                }

                // needed for the save as, it throws an error if we pass id
                delete customInsight.id;

                return this.aiApi.createCustomAnalysis(customInsight.toApiModel()).pipe(
                    map(response => this.readCreatedInsightId(response)),
                    tap(() => {
                        if (emitSuccessMsg) {
                            this.messageService.add({
                                severity: messageSeverityEnum.success,
                                summary: this.messageTemplates[messageContext].create.summary.success,
                                detail: this.localizationService.transform(this.messageTemplates[messageContext].create.detail.success, {
                                    name: customInsight.name
                                }),
                            });
                        }
                        this.refreshCustomInsightsSubject.next();
                    }),
                    catchError(x => {
                        const detail = x?.error?.details?.[0]?.message;

                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: this.messageTemplates[messageContext].create.summary.fail,
                            detail: detail || this.localizationService.transform(this.messageTemplates[messageContext].create.detail.fail, {
                                name: customInsight.name
                            }),
                        });

                        return of(null);
                    })
                );
            })
        );
    }

    updateCustomInsight(customInsight: CustomInsights, messageContext = InsightContext.AiInsight): Observable<boolean> {
        return this.hasDuplicateCustomInsightName(customInsight).pipe(
            switchMap(isDuplicate => {
                if (isDuplicate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform(this.messageTemplates[messageContext].duplicate.summary.update, {
                            name: customInsight.name
                        }),
                        detail: this.localizationService.transform(this.messageTemplates[messageContext].duplicate.detail, {
                            name: customInsight.name
                        }),
                    });

                    return of(false);
                }

                return this.aiApi.updateCustomAnalysis(customInsight.toApiModel()).pipe(
                    map(() => {
                        this.messageService.add({
                            severity: messageSeverityEnum.success,
                            summary: this.messageTemplates[messageContext].update.summary.success,
                            detail: this.localizationService.transform(this.messageTemplates[messageContext].update.detail.success, {
                                name: customInsight.name
                            }),
                        });
                        this.refreshCustomInsightsSubject.next();
                        return true;
                    }),
                    catchError(() => {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: this.messageTemplates[messageContext].update.summary.fail,
                            detail: this.localizationService.transform(this.messageTemplates[messageContext].update.detail.fail, { name: customInsight.name }),
                        });
                        return of(false);
                    })
                );
            })
        );
    }

    deleteCustomInsight(customInsight: CustomInsights, messageContext = InsightContext.AiInsight): void {
        this.aiApi.deleteCustomAnalysis(customInsight.id).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.messageTemplates[messageContext].delete.summary.success,
                    detail: this.localizationService.transform(this.messageTemplates[messageContext].delete.detail.success, { name: customInsight.name }),
                });

                this.refreshCustomInsightsSubject.next();

            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.messageTemplates[messageContext].delete.summary.fail,
                    detail: this.localizationService.transform(this.messageTemplates[messageContext].delete.detail.fail, { name: customInsight.name }),
                });
            }
        });
    }

    getKeyMessages(coverageInsights: CoverageInsights, searchRequest: MentionSearchCriteriaModel): Observable<KeyMessageGroupApi[]>  {
        const payload = coverageInsights.toApiModel();
        payload.isAccountSearch = searchRequest.isAccountSearch;
        payload.searchRequest = searchRequest.getApiEntity();

        return this.aiApi.getKeyMessages(payload);
    }

    getUserDefinedKeyMessages(
        coverageInsights: CoverageInsights,
        searchRequest: MentionSearchCriteriaModel,
        isAccountSearch: boolean
    ): Observable<KeyMessageApi[]>  {
        const payload = coverageInsights.toApiModel();
        payload.isAccountSearch = isAccountSearch;
        payload.searchRequest = searchRequest.getApiEntity();

        return this.aiApi.getUserDefinedKeyMessages(payload);
    }

    generateCoverageInsight(coverageInsights: CoverageInsights, mentionIds: string[], isAccountSearch: boolean): Observable<any> {
        const payload = coverageInsights.toApiModel();
        payload.isAccountSearch = isAccountSearch;
        payload.mentionIds = mentionIds.slice(0, 200);

        return this.aiApi.analysis(payload);
    }

    /** The create endpoint answers with the id of the new insight, either as a plain value or as part of the created insight */
    private readCreatedInsightId(response: any): number {
        const id = typeof response === 'object' && response !== null ? response.id : response;

        return Number.isFinite(Number(id)) && Number(id) > 0 ? Number(id) : null;
    }

    private hasDuplicateCustomInsightName(customInsight: CustomInsights): Observable<boolean> {
        const normalizedName = customInsight?.name?.trim()?.toLowerCase();

        if (!normalizedName || !customInsight?.promptType) {
            return of(false);
        }

        return this.customInsights$.pipe(
            take(1),
            map(customInsights => customInsights.some(existingInsight =>
                existingInsight.promptType === customInsight.promptType
                && existingInsight.name?.trim()?.toLowerCase() === normalizedName
                && existingInsight.id !== customInsight.id
            ))
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
